import type { AxiosInstance } from "axios";
import { getAuth0Users } from "settings/account/components/Users/request/request";
import type { ScopeCustomer, User } from "settings/account/components/TagsManager/types";

interface CustomerRow {
  id: number;
  nameDisplay: string;
  Domain?: string;
  customerId?: string | null;
}

interface RawAuth0User {
  user_id: string;
  name?: string;
  email?: string;
}

interface PartnerRow {
  id: number;
  nameDisplay: string;
  Domain?: string;
}

/** Same shape as `ScopeCustomer` but carrying the bits the SideNav dispatches. */
export interface PickerEntry extends ScopeCustomer {
  domain?: string;
  customerId?: string | null;
  /** true when this row is itself a partner tenant (not a customer under a partner). */
  isPartner?: boolean;
}

/**
 * All partner tenants visible to a GLOBAL_ADMIN (same API as SideNav `getPartners`).
 * Includes the GLOBAL_ADMIN's own tenant when `includeGlobalAdmin` is true.
 */
export async function listPartners(
  api: AxiosInstance,
  includeGlobalAdmin = true
): Promise<PickerEntry[]> {
  const resp = await api.get<{ data?: PartnerRow[] }>("/tenant/partners", {
    params: includeGlobalAdmin ? { includeGlobalAdmin: true } : undefined,
  });
  const raw = resp.data?.data;
  if (!Array.isArray(raw)) return [];
  return raw.map((p) => ({
    id: p.id,
    name: p.nameDisplay,
    domain: p.Domain ?? "",
    isPartner: true,
  }));
}

/**
 * Customers visible under a partner tenant (same API as SideNav `getCustomers`).
 * `partnerTenantId` is the partner's chopper tenant id (parent), not a customer id.
 */
export async function listCustomersUnderPartner(
  api: AxiosInstance,
  partnerTenantId: number
): Promise<PickerEntry[]> {
  if (!partnerTenantId) return [];
  const resp = await api.get<{ data?: CustomerRow[] }>(
    `/tenant/customers?tenantId=${partnerTenantId}`
  );
  const raw = resp.data?.data;
  if (!Array.isArray(raw)) return [];
  return raw.map((c) => ({
    id: c.id,
    name: c.nameDisplay,
    domain: c.Domain ?? "",
    customerId: c.customerId ?? null,
    isPartner: false,
  }));
}

/**
 * Platform users for a customer tenant — wraps `GET /v1/auth0/users/list/?tenantId=`.
 * `tenantId` must be the selected customer's tenant id (same as SideNav context).
 */
export async function listPlatformUsersForTenant(
  token: string,
  tenantId: number,
  opts?: { search?: string }
): Promise<User[]> {
  if (!tenantId) return [];
  const resp = await getAuth0Users(token, tenantId);
  const raw = resp.data as { users?: RawAuth0User[] };
  const userList = Array.isArray(raw?.users) ? raw.users : [];
  let users: User[] = userList
    .filter((u) => u.email && u.user_id)
    .map((u) => ({
      auth0Id: u.user_id,
      email: u.email as string,
      displayName: u.name?.trim() ? u.name.trim() : null,
      customerId: tenantId,
    }));
  const q = opts?.search?.trim().toLowerCase();
  if (q) {
    users = users.filter((u) =>
      [u.email, u.displayName ?? ""].join(" ").toLowerCase().includes(q)
    );
  }
  return users;
}
